/**
 * LDAP/LDAPS Scan Panel Component
 *
 * Comprehensive LDAP penetration testing interface with:
 * - Scan configuration and execution
 * - Real-time progress monitoring
 * - Vulnerability detection display
 * - User/Group/Computer enumeration results
 * - Certificate analysis (ESC1-ESC8)
 * - BloodHound attack path visualization
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Server,
  Shield,
  Users,
  Monitor,
  Key,
  Lock,
  Unlock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Play,
  Square,
  RefreshCw,
  FileText,
  Target,
  Network,
  ChevronDown,
  ChevronRight,
  Info,
  ExternalLink,
  Search,
  Settings,
  Clock,
  TrendingUp,
  Activity,
  Database,
  Folder,
  Award,
  Zap,
  Eye,
  Download,
  Copy,
  Brain,
  Terminal,
  Camera,
  Hash,
  Cpu,
  Loader2,
  Image,
  Link2,
  Bug,
  BookOpen
} from 'lucide-react';
import api from '../../services/api/client';

// Severity colors
const SEVERITY_COLORS = {
  critical: 'bg-red-500 text-white',
  high: 'bg-orange-500 text-white',
  medium: 'bg-yellow-500 text-black',
  low: 'bg-blue-500 text-white',
  info: 'bg-gray-500 text-white'
};

const SEVERITY_BG = {
  critical: 'bg-red-500/10 border-red-500/30',
  high: 'bg-orange-500/10 border-orange-500/30',
  medium: 'bg-yellow-500/10 border-yellow-500/30',
  low: 'bg-blue-500/10 border-blue-500/30',
  info: 'bg-gray-500/10 border-gray-500/30'
};

// LDAP Scan Configuration Form
const LDAPScanForm = ({ onStartScan, isScanning }) => {
  const [config, setConfig] = useState({
    target_host: '',
    target_port: 389,
    protocol: 'ldap',
    domain: '',
    base_dn: '',
    username: '',
    password: '',
    scan_type: 'standard',
    check_anonymous_bind: true,
    enumerate_users: true,
    enumerate_groups: true,
    enumerate_computers: true,
    check_certificates: true,
    check_delegation: true,
    check_password_policy: true,
    check_gpos: true,
    run_bloodhound: false
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));

    // Auto-update port based on protocol
    if (field === 'protocol') {
      if (value === 'ldaps') {
        setConfig(prev => ({ ...prev, target_port: 636 }));
      } else {
        setConfig(prev => ({ ...prev, target_port: 389 }));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onStartScan(config);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Target Configuration */}
      <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-cyan-400" />
          Configuration Cible
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Serveur LDAP / DC IP *
            </label>
            <input
              type="text"
              value={config.target_host}
              onChange={(e) => handleChange('target_host', e.target.value)}
              placeholder="192.168.1.10 ou dc01.corp.local"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Protocole
            </label>
            <select
              value={config.protocol}
              onChange={(e) => handleChange('protocol', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
            >
              <option value="ldap">LDAP (389)</option>
              <option value="ldaps">LDAPS (636)</option>
              <option value="starttls">LDAP + STARTTLS</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Port
            </label>
            <input
              type="number"
              value={config.target_port}
              onChange={(e) => handleChange('target_port', parseInt(e.target.value))}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Domaine AD
            </label>
            <input
              type="text"
              value={config.domain}
              onChange={(e) => handleChange('domain', e.target.value)}
              placeholder="corp.local"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Base DN (optionnel)
            </label>
            <input
              type="text"
              value={config.base_dn}
              onChange={(e) => handleChange('base_dn', e.target.value)}
              placeholder="DC=corp,DC=local"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Type de Scan
            </label>
            <select
              value={config.scan_type}
              onChange={(e) => handleChange('scan_type', e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
            >
              <option value="quick">Quick - Basic enumeration</option>
              <option value="standard">Standard - Pentest complet</option>
              <option value="full">Complet - Audit exhaustif</option>
              <option value="stealth">Furtif - Profil bas</option>
            </select>
          </div>
        </div>
      </div>

      {/* Authentication */}
      <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Key className="w-5 h-5 text-yellow-400" />
          Authentification (optionnel)
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Utilisateur
            </label>
            <input
              type="text"
              value={config.username}
              onChange={(e) => handleChange('username', e.target.value)}
              placeholder="user@corp.local ou CORP\user"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Mot de passe
            </label>
            <input
              type="password"
              value={config.password}
              onChange={(e) => handleChange('password', e.target.value)}
              placeholder="********"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
            />
          </div>
        </div>

        <p className="text-sm text-gray-400 mt-2">
          Sans credentials, seules les attaques anonymes seront testees (Anonymous Bind, Null sessions...)
        </p>
      </div>

      {/* Advanced Options */}
      <div className="bg-gray-800/50 rounded-lg border border-gray-700">
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="w-full p-4 flex items-center justify-between text-white hover:bg-gray-700/50 rounded-lg transition-colors"
        >
          <span className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-gray-400" />
            Advanced Options
          </span>
          {showAdvanced ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>

        {showAdvanced && (
          <div className="p-4 pt-0 border-t border-gray-700">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[
                { key: 'check_anonymous_bind', label: 'Test Anonymous Bind', icon: Unlock },
                { key: 'enumerate_users', label: 'Enumerate Users', icon: Users },
                { key: 'enumerate_groups', label: 'Enumerate Groups', icon: Folder },
                { key: 'enumerate_computers', label: 'Enumerate Computers', icon: Monitor },
                { key: 'check_certificates', label: 'Analyser Certificats (ESC1-8)', icon: Award },
                { key: 'check_delegation', label: 'Check Delegation', icon: Network },
                { key: 'check_password_policy', label: 'Politique Mots de Passe', icon: Lock },
                { key: 'check_gpos', label: 'Enumerate GPOs', icon: FileText },
                { key: 'run_bloodhound', label: 'Collection BloodHound', icon: Zap }
              ].map(({ key, label, icon: Icon }) => (
                <label key={key} className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={config[key]}
                    onChange={(e) => handleChange(key, e.target.checked)}
                    className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-cyan-500 focus:ring-cyan-500"
                  />
                  <Icon className="w-4 h-4 text-gray-400 group-hover:text-cyan-400" />
                  <span className="text-sm text-gray-300 group-hover:text-white">{label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isScanning || !config.target_host}
          className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-all ${
            isScanning || !config.target_host
              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-400 hover:to-blue-500 shadow-lg shadow-cyan-500/25'
          }`}
        >
          {isScanning ? (
            <>
              <RefreshCw className="w-5 h-5 animate-spin" />
              Scan en cours...
            </>
          ) : (
            <>
              <Play className="w-5 h-5" />
              Start LDAP Scan
            </>
          )}
        </button>
      </div>
    </form>
  );
};

// Progress Bar Component
const ProgressBar = ({ progress, phase }) => (
  <div className="space-y-2">
    <div className="flex justify-between text-sm">
      <span className="text-gray-400">Phase: {phase}</span>
      <span className="text-cyan-400">{progress}%</span>
    </div>
    <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-500"
        style={{ width: `${progress}%` }}
      />
    </div>
  </div>
);

// Vulnerability Card
const VulnerabilityCard = ({ vulnerability }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`rounded-lg border p-4 ${SEVERITY_BG[vulnerability.severity]}`}>
      <div
        className="flex items-start justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start gap-3">
          <span className={`px-2 py-1 rounded text-xs font-bold ${SEVERITY_COLORS[vulnerability.severity]}`}>
            {vulnerability.severity.toUpperCase()}
          </span>
          <div>
            <h4 className="font-semibold text-white">{vulnerability.name}</h4>
            <p className="text-sm text-gray-400 mt-1">{vulnerability.id}</p>
          </div>
        </div>
        {expanded ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
      </div>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-gray-700 space-y-3">
          <div>
            <h5 className="text-sm font-medium text-gray-300">Description</h5>
            <p className="text-sm text-gray-400 mt-1">{vulnerability.description}</p>
          </div>

          {vulnerability.affected_objects?.length > 0 && (
            <div>
              <h5 className="text-sm font-medium text-gray-300">Objets Affectes</h5>
              <div className="flex flex-wrap gap-2 mt-1">
                {vulnerability.affected_objects.map((obj, idx) => (
                  <span key={idx} className="px-2 py-1 bg-gray-700 rounded text-xs text-gray-300">
                    {obj}
                  </span>
                ))}
              </div>
            </div>
          )}

          {vulnerability.remediation && (
            <div>
              <h5 className="text-sm font-medium text-gray-300">Remediation</h5>
              <p className="text-sm text-gray-400 mt-1">{vulnerability.remediation}</p>
            </div>
          )}

          {vulnerability.mitre_id && (
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-purple-500/20 border border-purple-500/30 rounded text-xs text-purple-300">
                MITRE ATT&CK: {vulnerability.mitre_id}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// AI Exploitation Panel Component
const AIExploitationPanel = ({ vulnerability, targetHost, targetPort, domain, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [exploitResult, setExploitResult] = useState(null);
  const [playbook, setPlaybook] = useState(null);
  const [error, setError] = useState(null);

  // Fetch AI exploitation assistance
  const fetchAIExploitation = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.post('/ldap-pentest/ai/exploit-assistance', {
        vulnerability_id: vulnerability.id,
        vulnerability_name: vulnerability.name,
        severity: vulnerability.severity,
        description: vulnerability.description,
        target_host: targetHost,
        target_port: targetPort,
        domain: domain,
        affected_objects: vulnerability.affected_objects || [],
        context: `LDAP/AD vulnerability exploitation guidance`,
        language: 'fr'
      });
      setExploitResult(response.data);
    } catch (err) {
      // Don't show errors for auth redirects (handled by API client)
      if (!err.isAuthRedirect) {
        setError(err.response?.data?.detail || 'Erreur lors de l\'analyse IA');
      }
    }
    setLoading(false);
  };

  // Fetch playbook
  const fetchPlaybook = async () => {
    try {
      const response = await api.get(`/ldap-pentest/ai/playbook/${vulnerability.id}`);
      setPlaybook(response.data);
    } catch (err) {
      // Don't log errors for auth redirects (handled by API client)
      if (!err.isAuthRedirect) {
        console.error('Playbook not found:', err);
      }
    }
  };

  useEffect(() => {
    fetchPlaybook();
    fetchAIExploitation();
  }, [vulnerability.id]);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-xl border border-gray-700 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-700 flex items-center justify-between bg-gradient-to-r from-purple-900/50 to-cyan-900/50">
          <div className="flex items-center gap-3">
            <Brain className="w-6 h-6 text-purple-400" />
            <div>
              <h3 className="text-lg font-bold text-white">Assistance IA - Exploitation</h3>
              <p className="text-sm text-gray-400">{vulnerability.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white">
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
              <span className="ml-4 text-gray-400">Analyse IA en cours...</span>
            </div>
          )}

          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
              {error}
            </div>
          )}

          {/* Playbook */}
          {playbook && (
            <div className="bg-gray-800/50 rounded-lg p-4 border border-purple-500/30">
              <h4 className="font-semibold text-white flex items-center gap-2 mb-4">
                <BookOpen className="w-5 h-5 text-purple-400" />
                Playbook: {playbook.name}
              </h4>
              {playbook.mitre_id && (
                <span className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded mb-4 inline-block">
                  MITRE ATT&CK: {playbook.mitre_id}
                </span>
              )}
              <div className="space-y-3 mt-4">
                {playbook.steps?.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-500/20 text-purple-300 flex items-center justify-center text-sm font-bold">
                      {idx + 1}
                    </span>
                    <div className="flex-1">
                      <p className="text-gray-300">{step.description}</p>
                      {step.command && (
                        <div className="mt-2 flex items-center gap-2">
                          <code className="flex-1 text-xs bg-gray-900 rounded p-2 text-cyan-300 font-mono overflow-x-auto">
                            {step.command}
                          </code>
                          <button
                            onClick={() => navigator.clipboard.writeText(step.command)}
                            className="p-2 text-gray-400 hover:text-white"
                            title="Copier"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Analysis */}
          {exploitResult && !loading && (
            <>
              {/* Explanation */}
              <div className="bg-gray-800/50 rounded-lg p-4 border border-cyan-500/30">
                <h4 className="font-semibold text-white flex items-center gap-2 mb-3">
                  <Bug className="w-5 h-5 text-cyan-400" />
                  Explication de la Vulnerabilite
                </h4>
                <p className="text-gray-300 whitespace-pre-wrap">{exploitResult.explanation}</p>
              </div>

              {/* Exploitation Steps */}
              {exploitResult.exploitation_steps?.length > 0 && (
                <div className="bg-gray-800/50 rounded-lg p-4 border border-orange-500/30">
                  <h4 className="font-semibold text-white flex items-center gap-2 mb-4">
                    <Terminal className="w-5 h-5 text-orange-400" />
                    Etapes d'Exploitation
                  </h4>
                  <div className="space-y-4">
                    {exploitResult.exploitation_steps.map((step, idx) => (
                      <div key={idx} className="bg-gray-900/50 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-orange-500/20 text-orange-300 flex items-center justify-center font-bold">
                            {idx + 1}
                          </span>
                          <div className="flex-1">
                            <h5 className="font-semibold text-white">{step.title}</h5>
                            <p className="text-sm text-gray-400 mt-1">{step.description}</p>
                            {step.command && (
                              <div className="mt-3 flex items-center gap-2">
                                <code className="flex-1 text-xs bg-black rounded p-3 text-green-400 font-mono overflow-x-auto">
                                  {step.command}
                                </code>
                                <button
                                  onClick={() => navigator.clipboard.writeText(step.command)}
                                  className="p-2 text-gray-400 hover:text-white"
                                >
                                  <Copy className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommended Tools */}
              {exploitResult.recommended_tools?.length > 0 && (
                <div className="bg-gray-800/50 rounded-lg p-4 border border-green-500/30">
                  <h4 className="font-semibold text-white flex items-center gap-2 mb-3">
                    <Zap className="w-5 h-5 text-green-400" />
                    Outils Recommandes
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {exploitResult.recommended_tools.map((tool, idx) => (
                      <span key={idx} className="px-3 py-1 bg-green-500/20 text-green-300 rounded-lg text-sm">
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Risk Assessment */}
              {exploitResult.risk_assessment && (
                <div className="bg-gray-800/50 rounded-lg p-4 border border-red-500/30">
                  <h4 className="font-semibold text-white flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    Evaluation des Risques
                  </h4>
                  <p className="text-gray-300">{exploitResult.risk_assessment}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
          <button
            onClick={fetchAIExploitation}
            disabled={loading}
            className="px-4 py-2 bg-purple-500/20 border border-purple-500/30 text-purple-300 rounded-lg hover:bg-purple-500/30 flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Regenerate Analysis
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

// Password Cracking Panel Component
const PasswordCrackingPanel = ({ scanId, workspaceId }) => {
  const [hashes, setHashes] = useState('');
  const [hashType, setHashType] = useState('ntlm');
  const [wordlist, setWordlist] = useState('rockyou');
  const [rules, setRules] = useState('');
  const [crackJob, setCrackJob] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hashTypes, setHashTypes] = useState([]);
  const [wordlists, setWordlists] = useState([]);

  // Fetch available hash types and wordlists
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [hashTypesRes, wordlistsRes] = await Promise.all([
          api.get('/ldap-pentest/password/hash-types'),
          api.get('/ldap-pentest/password/wordlists')
        ]);
        setHashTypes(hashTypesRes.data.hash_types || []);
        setWordlists(wordlistsRes.data.wordlists || []);
      } catch (err) {
        // Don't log errors for auth redirects (handled by API client)
        if (!err.isAuthRedirect) {
          console.error('Failed to fetch options:', err);
        }
      }
    };
    fetchOptions();
  }, []);

  // Poll crack job status
  useEffect(() => {
    let interval;
    if (crackJob?.job_id && crackJob.status === 'running') {
      interval = setInterval(async () => {
        try {
          const response = await api.get(`/ldap-pentest/password/crack/${crackJob.job_id}`);
          setCrackJob(response.data);

          if (response.data.status === 'completed' || response.data.status === 'failed') {
            // Fetch results
            const resultsRes = await api.get(`/ldap-pentest/password/crack/${crackJob.job_id}/results`);
            setResults(resultsRes.data.cracked_passwords || []);
          }
        } catch (err) {
          // Don't log errors for auth redirects (handled by API client)
          if (!err.isAuthRedirect) {
            console.error('Failed to fetch job status:', err);
          }
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [crackJob?.job_id, crackJob?.status]);

  const startCracking = async () => {
    setLoading(true);
    setError(null);
    setResults([]);

    try {
      const hashList = hashes.split('\n').filter(h => h.trim());
      const response = await api.post('/ldap-pentest/password/crack', {
        hash_type: hashType,
        hashes: hashList,
        wordlist: wordlist,
        rules: rules || null,
        scan_id: scanId,
        workspace_id: workspaceId
      });
      setCrackJob(response.data);
    } catch (err) {
      // Don't show errors for auth redirects (handled by API client)
      if (!err.isAuthRedirect) {
        setError(err.response?.data?.detail || 'Erreur lors du demarrage du cracking');
      }
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-orange-900/30 to-red-900/30 rounded-lg p-4 border border-orange-500/30">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Hash className="w-5 h-5 text-orange-400" />
          Password Cracking (Hashcat)
        </h3>
        <p className="text-sm text-gray-400 mt-1">
          Craquez les hashs captures (NTLM, Kerberos, etc.) avec hashcat
        </p>
      </div>

      {/* Configuration */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Type de Hash</label>
          <select
            value={hashType}
            onChange={(e) => setHashType(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
          >
            {hashTypes.length > 0 ? hashTypes.map(ht => (
              <option key={ht.id} value={ht.id}>{ht.name} ({ht.hashcat_mode})</option>
            )) : (
              <>
                <option value="ntlm">NTLM (1000)</option>
                <option value="krb5asrep">Kerberos AS-REP (18200)</option>
                <option value="krb5tgs">Kerberos TGS (13100)</option>
              </>
            )}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Wordlist</label>
          <select
            value={wordlist}
            onChange={(e) => setWordlist(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
          >
            {wordlists.length > 0 ? wordlists.map(wl => (
              <option key={wl.name} value={wl.name}>{wl.name} ({wl.size})</option>
            )) : (
              <>
                <option value="rockyou">rockyou.txt</option>
                <option value="common-passwords">common-passwords.txt</option>
                <option value="custom">custom wordlist</option>
              </>
            )}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Rules (optionnel)</label>
          <input
            type="text"
            value={rules}
            onChange={(e) => setRules(e.target.value)}
            placeholder="best64, d3ad0ne..."
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400"
          />
        </div>
      </div>

      {/* Hash Input */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Hashs (un par ligne)
        </label>
        <textarea
          value={hashes}
          onChange={(e) => setHashes(e.target.value)}
          rows={6}
          placeholder="Collez vos hashs ici (NTLM, AS-REP, TGS...)&#10;$krb5asrep$23$user@DOMAIN.LOCAL:...&#10;aad3b435b51404eeaad3b435b51404ee:..."
          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white font-mono text-sm placeholder-gray-400"
        />
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Start Button */}
      <button
        onClick={startCracking}
        disabled={loading || !hashes.trim()}
        className={`w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 ${
          loading || !hashes.trim()
            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
            : 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-400 hover:to-red-400'
        }`}
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Demarrage...
          </>
        ) : (
          <>
            <Cpu className="w-5 h-5" />
            Lancer le Cracking
          </>
        )}
      </button>

      {/* Job Status */}
      {crackJob && (
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-white">Job: {crackJob.job_id?.substring(0, 8)}...</h4>
            <span className={`px-2 py-1 rounded text-xs font-bold ${
              crackJob.status === 'completed' ? 'bg-green-500/20 text-green-300' :
              crackJob.status === 'running' ? 'bg-blue-500/20 text-blue-300' :
              crackJob.status === 'failed' ? 'bg-red-500/20 text-red-300' :
              'bg-gray-500/20 text-gray-300'
            }`}>
              {crackJob.status}
            </span>
          </div>

          {crackJob.progress !== undefined && (
            <div className="mb-3">
              <div className="flex justify-between text-sm text-gray-400 mb-1">
                <span>Progression</span>
                <span>{crackJob.progress}%</span>
              </div>
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all"
                  style={{ width: `${crackJob.progress}%` }}
                />
              </div>
            </div>
          )}

          {crackJob.stats && (
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-green-400">{crackJob.stats.cracked || 0}</p>
                <p className="text-xs text-gray-400">Crackes</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-400">{crackJob.stats.remaining || 0}</p>
                <p className="text-xs text-gray-400">Restants</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-cyan-400">{crackJob.stats.speed || '0 H/s'}</p>
                <p className="text-xs text-gray-400">Vitesse</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="bg-gray-800/50 rounded-lg p-4 border border-green-500/30">
          <h4 className="font-semibold text-white flex items-center gap-2 mb-4">
            <CheckCircle className="w-5 h-5 text-green-400" />
            Mots de Passe Crackes ({results.length})
          </h4>
          <div className="space-y-2">
            {results.map((result, idx) => (
              <div key={idx} className="flex items-center justify-between bg-gray-900/50 rounded-lg p-3">
                <div>
                  <p className="text-white font-mono">{result.username || result.hash?.substring(0, 32)}...</p>
                  <p className="text-green-400 font-bold">{result.password}</p>
                </div>
                <button
                  onClick={() => navigator.clipboard.writeText(`${result.username}:${result.password}`)}
                  className="p-2 text-gray-400 hover:text-white"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Remote Connection Panel Component
const RemoteConnectionPanel = ({ targetHost, domain, workspaceId, credentials }) => {
  const [protocol, setProtocol] = useState('smb');
  const [host, setHost] = useState(targetHost || '');
  const [port, setPort] = useState(445);
  const [username, setUsername] = useState(credentials?.username || '');
  const [password, setPassword] = useState(credentials?.password || '');
  const [ntlmHash, setNtlmHash] = useState('');
  const [commands, setCommands] = useState('');
  const [captureScreenshot, setCaptureScreenshot] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [session, setSession] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [error, setError] = useState(null);
  const [protocols, setProtocols] = useState([]);

  // Fetch supported protocols
  useEffect(() => {
    const fetchProtocols = async () => {
      try {
        const response = await api.get('/ldap-pentest/connect/protocols');
        setProtocols(response.data.protocols || []);
      } catch (err) {
        // Don't log errors for auth redirects (handled by API client)
        if (!err.isAuthRedirect) {
          console.error('Failed to fetch protocols:', err);
        }
      }
    };
    fetchProtocols();
  }, []);

  // Update port based on protocol
  useEffect(() => {
    const portMap = {
      smb: 445,
      winrm: 5985,
      ssh: 22,
      ldap: 389,
      rdp: 3389
    };
    setPort(portMap[protocol] || 445);
  }, [protocol]);

  // Poll session status
  useEffect(() => {
    let interval;
    if (session?.session_id && session.status === 'connecting') {
      interval = setInterval(async () => {
        try {
          const response = await api.get(`/ldap-pentest/connect/${session.session_id}`);
          setSession(response.data);

          if (response.data.status === 'connected' || response.data.status === 'completed' || response.data.status === 'failed') {
            // Fetch evidence
            const evidenceRes = await api.get(`/ldap-pentest/connect/${session.session_id}/evidence`);
            setEvidence(evidenceRes.data.evidence || []);
          }
        } catch (err) {
          // Don't log errors for auth redirects (handled by API client)
          if (!err.isAuthRedirect) {
            console.error('Failed to fetch session status:', err);
          }
        }
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [session?.session_id, session?.status]);

  const connect = async () => {
    setConnecting(true);
    setError(null);
    setEvidence([]);

    try {
      const cmdList = commands.split('\n').filter(c => c.trim());
      const response = await api.post('/ldap-pentest/connect/remote', {
        target_host: host,
        target_port: port,
        protocol: protocol,
        username: username,
        password: password || null,
        ntlm_hash: ntlmHash || null,
        domain: domain,
        workspace_id: workspaceId,
        commands: cmdList,
        capture_screenshot: captureScreenshot
      });
      setSession(response.data);
    } catch (err) {
      // Don't show errors for auth redirects (handled by API client)
      if (!err.isAuthRedirect) {
        setError(err.response?.data?.detail || 'Erreur de connexion');
      }
    }
    setConnecting(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-green-900/30 to-cyan-900/30 rounded-lg p-4 border border-green-500/30">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Link2 className="w-5 h-5 text-green-400" />
          Connexion Remote avec Capture de Preuves
        </h3>
        <p className="text-sm text-gray-400 mt-1">
          Connectez-vous aux systemes compromis et capturez des preuves (screenshots, outputs)
        </p>
      </div>

      {/* Configuration */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Protocole</label>
          <select
            value={protocol}
            onChange={(e) => setProtocol(e.target.value)}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
          >
            {protocols.length > 0 ? protocols.map(p => (
              <option key={p.id} value={p.id}>{p.name} ({p.default_port})</option>
            )) : (
              <>
                <option value="smb">SMB (445)</option>
                <option value="winrm">WinRM (5985)</option>
                <option value="ssh">SSH (22)</option>
                <option value="ldap">LDAP Bind (389)</option>
              </>
            )}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Cible</label>
          <input
            type="text"
            value={host}
            onChange={(e) => setHost(e.target.value)}
            placeholder="192.168.1.10"
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Port</label>
          <input
            type="number"
            value={port}
            onChange={(e) => setPort(parseInt(e.target.value))}
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Domaine</label>
          <input
            type="text"
            value={domain}
            onChange={() => {}}
            placeholder="CORP"
            className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400"
          />
        </div>
      </div>

      {/* Credentials */}
      <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
        <h4 className="font-semibold text-white mb-4 flex items-center gap-2">
          <Key className="w-5 h-5 text-yellow-400" />
          Credentials
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Utilisateur</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="administrator"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Mot de passe</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="********"
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white placeholder-gray-400"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">NTLM Hash (optionnel)</label>
            <input
              type="text"
              value={ntlmHash}
              onChange={(e) => setNtlmHash(e.target.value)}
              placeholder="aad3b435b51404ee:..."
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white font-mono text-sm placeholder-gray-400"
            />
          </div>
        </div>
      </div>

      {/* Commands */}
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-1">
          Commandes a executer (une par ligne)
        </label>
        <textarea
          value={commands}
          onChange={(e) => setCommands(e.target.value)}
          rows={4}
          placeholder="whoami&#10;hostname&#10;ipconfig /all&#10;net user"
          className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white font-mono text-sm placeholder-gray-400"
        />
      </div>

      {/* Options */}
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={captureScreenshot}
            onChange={(e) => setCaptureScreenshot(e.target.checked)}
            className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-cyan-500"
          />
          <Camera className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-300">Capturer des screenshots</span>
        </label>
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Connect Button */}
      <button
        onClick={connect}
        disabled={connecting || !host || !username}
        className={`w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 ${
          connecting || !host || !username
            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
            : 'bg-gradient-to-r from-green-500 to-cyan-500 text-white hover:from-green-400 hover:to-cyan-400'
        }`}
      >
        {connecting ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Connexion en cours...
          </>
        ) : (
          <>
            <Terminal className="w-5 h-5" />
            Connect and execute
          </>
        )}
      </button>

      {/* Session Status */}
      {session && (
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-semibold text-white">Session: {session.session_id?.substring(0, 8)}...</h4>
            <span className={`px-2 py-1 rounded text-xs font-bold ${
              session.status === 'connected' || session.status === 'completed' ? 'bg-green-500/20 text-green-300' :
              session.status === 'connecting' ? 'bg-blue-500/20 text-blue-300' :
              session.status === 'failed' ? 'bg-red-500/20 text-red-300' :
              'bg-gray-500/20 text-gray-300'
            }`}>
              {session.status}
            </span>
          </div>

          {/* Command Output */}
          {session.output && (
            <div className="mt-4">
              <h5 className="text-sm font-medium text-gray-300 mb-2">Output:</h5>
              <pre className="bg-black rounded-lg p-3 text-green-400 text-xs font-mono overflow-x-auto max-h-64 overflow-y-auto">
                {session.output}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* Evidence Gallery */}
      {evidence.length > 0 && (
        <div className="bg-gray-800/50 rounded-lg p-4 border border-cyan-500/30">
          <h4 className="font-semibold text-white flex items-center gap-2 mb-4">
            <Camera className="w-5 h-5 text-cyan-400" />
            Preuves Capturees ({evidence.length})
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {evidence.map((item, idx) => (
              <div key={idx} className="bg-gray-900/50 rounded-lg overflow-hidden border border-gray-700">
                {item.type === 'screenshot' && item.path && (
                  <img
                    src={`/api/v1/ldap-pentest/evidence/${item.path}`}
                    alt={`Evidence ${idx + 1}`}
                    className="w-full h-40 object-cover"
                  />
                )}
                <div className="p-3">
                  <p className="text-sm text-white font-medium">{item.description || `Evidence ${idx + 1}`}</p>
                  <p className="text-xs text-gray-400 mt-1">{item.timestamp}</p>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => window.open(`/api/v1/ldap-pentest/evidence/${item.path}`, '_blank')}
                      className="px-2 py-1 bg-cyan-500/20 text-cyan-300 rounded text-xs hover:bg-cyan-500/30 flex items-center gap-1"
                    >
                      <Eye className="w-3 h-3" /> Voir
                    </button>
                    <button
                      onClick={() => {
                        const a = document.createElement('a');
                        a.href = `/api/v1/ldap-pentest/evidence/${item.path}`;
                        a.download = item.filename || `evidence_${idx}.png`;
                        a.click();
                      }}
                      className="px-2 py-1 bg-gray-500/20 text-gray-300 rounded text-xs hover:bg-gray-500/30 flex items-center gap-1"
                    >
                      <Download className="w-3 h-3" /> Telecharger
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Results Section Component
const ResultsSection = ({ title, icon: Icon, count, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-gray-800/50 rounded-lg border border-gray-700">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-cyan-400" />
          <span className="font-semibold text-white">{title}</span>
          {count !== undefined && (
            <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-300 rounded-full text-sm">
              {count}
            </span>
          )}
        </div>
        {isOpen ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
      </button>

      {isOpen && (
        <div className="p-4 pt-0 border-t border-gray-700">
          {children}
        </div>
      )}
    </div>
  );
};

// Main LDAP Scan Panel Component
const LDAPScanPanel = ({ workspaceId }) => {
  const [activeTab, setActiveTab] = useState('config'); // config, results, tools, exploit, cracking, connect
  const [currentScan, setCurrentScan] = useState(null);
  const [scanResults, setScanResults] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [tools, setTools] = useState(null);
  const [scans, setScans] = useState([]);
  const [error, setError] = useState(null);
  const [selectedVulnerability, setSelectedVulnerability] = useState(null);
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [scanConfig, setScanConfig] = useState(null);

  // Fetch available tools
  useEffect(() => {
    const fetchTools = async () => {
      try {
        const response = await api.get('/ldap-pentest/tools');
        setTools(response.data);
      } catch (err) {
        // Don't log errors for auth redirects (handled by API client)
        if (!err.isAuthRedirect) {
          console.error('Failed to fetch LDAP tools:', err);
        }
      }
    };
    fetchTools();
  }, []);

  // Fetch previous scans
  useEffect(() => {
    const fetchScans = async () => {
      try {
        const response = await api.get('/ldap-pentest/scans');
        setScans(response.data.scans || []);
      } catch (err) {
        // Don't log errors for auth redirects (handled by API client)
        if (!err.isAuthRedirect) {
          console.error('Failed to fetch LDAP scans:', err);
        }
      }
    };
    fetchScans();
  }, []);

  // Poll scan status
  useEffect(() => {
    let interval;
    if (currentScan && isScanning) {
      interval = setInterval(async () => {
        try {
          const response = await api.get(`/ldap-pentest/scan/${currentScan}/status`);
          const status = response.data;

          if (status.status === 'completed' || status.status === 'failed' || status.status === 'stopped') {
            setIsScanning(false);
            // Fetch full results
            const resultsResponse = await api.get(`/ldap-pentest/scan/${currentScan}/results`);
            setScanResults(resultsResponse.data);
            setActiveTab('results');
          }
        } catch (err) {
          // Don't log errors for auth redirects (handled by API client)
          if (!err.isAuthRedirect) {
            console.error('Failed to fetch scan status:', err);
          }
        }
      }, 2000);
    }
    return () => clearInterval(interval);
  }, [currentScan, isScanning]);

  // Start new scan
  const handleStartScan = async (config) => {
    setError(null);
    setIsScanning(true);
    setScanResults(null);
    setScanConfig(config); // Save config for later use

    try {
      const response = await api.post('/ldap-pentest/scan/start', {
        ...config,
        workspace_id: workspaceId
      });

      if (response.data.success) {
        setCurrentScan(response.data.scan_id);
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      // Don't show errors for auth redirects (handled by API client)
      if (!err.isAuthRedirect) {
        setError(err.message || 'Failed to start scan');
      }
      setIsScanning(false);
    }
  };

  // Stop scan
  const handleStopScan = async () => {
    if (!currentScan) return;

    try {
      await api.post(`/ldap-pentest/scan/${currentScan}/stop`);
      setIsScanning(false);
    } catch (err) {
      // Don't log errors for auth redirects (handled by API client)
      if (!err.isAuthRedirect) {
        console.error('Failed to stop scan:', err);
      }
    }
  };

  // Load previous scan results
  const loadScanResults = async (scanId) => {
    try {
      const response = await api.get(`/ldap-pentest/scan/${scanId}/results`);
      setScanResults(response.data);
      setCurrentScan(scanId);
      setActiveTab('results');
    } catch (err) {
      // Don't show errors for auth redirects (handled by API client)
      if (!err.isAuthRedirect) {
        setError('Failed to load scan results');
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Database className="w-8 h-8 text-cyan-400" />
            LDAP/LDAPS Pentest
          </h2>
          <p className="text-gray-400 mt-1">
            Active Directory security audit via LDAP - Enumeration, Vulnerabilities, Certificates
          </p>
        </div>

        {isScanning && (
          <button
            onClick={handleStopScan}
            className="px-4 py-2 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 hover:bg-red-500/30 flex items-center gap-2"
          >
            <Square className="w-4 h-4" />
            Stop Scan
          </button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3 text-red-400">
          <AlertTriangle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Progress Display */}
      {isScanning && scanResults && (
        <div className="bg-gray-800/50 rounded-lg p-4 border border-cyan-500/30">
          <ProgressBar progress={scanResults.progress || 0} phase={scanResults.current_phase || 'Initialisation'} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-700 overflow-x-auto">
        {[
          { id: 'config', label: 'Configuration', icon: Settings },
          { id: 'results', label: 'Resultats', icon: FileText, disabled: !scanResults },
          { id: 'exploit', label: 'AI Exploitation', icon: Brain, disabled: !scanResults },
          { id: 'cracking', label: 'Password Cracking', icon: Hash },
          { id: 'connect', label: 'Connexion Remote', icon: Terminal },
          { id: 'tools', label: 'Outils LDAP', icon: Zap },
          { id: 'history', label: 'Historique', icon: Clock }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => !tab.disabled && setActiveTab(tab.id)}
            disabled={tab.disabled}
            className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-cyan-500 text-cyan-400'
                : tab.disabled
                ? 'border-transparent text-gray-600 cursor-not-allowed'
                : 'border-transparent text-gray-400 hover:text-white hover:border-gray-500'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="min-h-[500px]">
        {/* Configuration Tab */}
        {activeTab === 'config' && (
          <LDAPScanForm onStartScan={handleStartScan} isScanning={isScanning} />
        )}

        {/* Results Tab */}
        {activeTab === 'results' && scanResults && (
          <div className="space-y-4">
            {/* Scan Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: 'Vulnerabilities', value: scanResults.results?.vulnerabilities?.length || 0, icon: AlertTriangle, color: 'text-red-400' },
                { label: 'Utilisateurs', value: scanResults.results?.users?.length || 0, icon: Users, color: 'text-blue-400' },
                { label: 'Groupes', value: scanResults.results?.groups?.length || 0, icon: Folder, color: 'text-green-400' },
                { label: 'Ordinateurs', value: scanResults.results?.computers?.length || 0, icon: Monitor, color: 'text-purple-400' }
              ].map(stat => (
                <div key={stat.label} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                  <div className="flex items-center gap-3">
                    <stat.icon className={`w-8 h-8 ${stat.color}`} />
                    <div>
                      <p className="text-2xl font-bold text-white">{stat.value}</p>
                      <p className="text-sm text-gray-400">{stat.label}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Vulnerabilities */}
            <ResultsSection
              title="Detected Vulnerabilities"
              icon={Shield}
              count={scanResults.results?.vulnerabilities?.length || 0}
              defaultOpen={true}
            >
              {scanResults.results?.vulnerabilities?.length > 0 ? (
                <div className="space-y-3">
                  {scanResults.results.vulnerabilities.map((vuln, idx) => (
                    <VulnerabilityCard key={idx} vulnerability={vuln} />
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-4">No vulnerability detected</p>
              )}
            </ResultsSection>

            {/* Users */}
            <ResultsSection
              title="Utilisateurs"
              icon={Users}
              count={scanResults.results?.users?.length || 0}
            >
              {scanResults.results?.users?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-400 border-b border-gray-700">
                        <th className="pb-2">SAMAccountName</th>
                        <th className="pb-2">DN</th>
                        <th className="pb-2">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scanResults.results.users.map((user, idx) => (
                        <tr key={idx} className="border-b border-gray-700/50">
                          <td className="py-2 text-white font-mono">{user.samAccountName}</td>
                          <td className="py-2 text-gray-400 text-xs">{user.dn}</td>
                          <td className="py-2">
                            {user.enabled ? (
                              <span className="text-green-400 flex items-center gap-1">
                                <CheckCircle className="w-4 h-4" /> Actif
                              </span>
                            ) : (
                              <span className="text-red-400 flex items-center gap-1">
                                <XCircle className="w-4 h-4" /> Desactive
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-400 text-center py-4">Aucun utilisateur enumere</p>
              )}
            </ResultsSection>

            {/* Groups */}
            <ResultsSection
              title="Groupes"
              icon={Folder}
              count={scanResults.results?.groups?.length || 0}
            >
              {scanResults.results?.groups?.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {scanResults.results.groups.map((group, idx) => (
                    <div key={idx} className="bg-gray-700/50 rounded-lg p-3">
                      <p className="font-semibold text-white">{group.samAccountName}</p>
                      <p className="text-xs text-gray-400 mt-1">{group.dn}</p>
                      <p className="text-sm text-cyan-400 mt-2">{group.members} membres</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-center py-4">Aucun groupe enumere</p>
              )}
            </ResultsSection>

            {/* Password Policy */}
            {scanResults.results?.password_policy && (
              <ResultsSection title="Politique de Mots de Passe" icon={Lock}>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(scanResults.results.password_policy).map(([key, value]) => (
                    <div key={key} className="bg-gray-700/50 rounded-lg p-3">
                      <p className="text-sm text-gray-400">{key.replace(/_/g, ' ')}</p>
                      <p className="text-lg font-semibold text-white mt-1">{String(value)}</p>
                    </div>
                  ))}
                </div>
              </ResultsSection>
            )}

            {/* Certificates */}
            {scanResults.results?.certificates?.length > 0 && (
              <ResultsSection
                title="Templates de Certificats"
                icon={Award}
                count={scanResults.results.certificates.length}
              >
                <div className="space-y-3">
                  {scanResults.results.certificates.map((cert, idx) => (
                    <div
                      key={idx}
                      className={`rounded-lg p-3 ${cert.vulnerable ? 'bg-red-500/10 border border-red-500/30' : 'bg-gray-700/50'}`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-white">{cert.name}</p>
                        {cert.vulnerable && (
                          <span className="px-2 py-1 bg-red-500 text-white text-xs rounded font-bold">
                            VULNERABLE
                          </span>
                        )}
                      </div>
                      {cert.vulnerabilities?.length > 0 && (
                        <div className="flex gap-2 mt-2">
                          {cert.vulnerabilities.map((v, i) => (
                            <span key={i} className="px-2 py-1 bg-red-500/20 text-red-300 text-xs rounded">
                              {v}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ResultsSection>
            )}

            {/* Timeline */}
            {scanResults.timeline?.length > 0 && (
              <ResultsSection
                title="Timeline"
                icon={Clock}
                count={scanResults.timeline.length}
              >
                <div className="space-y-2">
                  {scanResults.timeline.map((event, idx) => (
                    <div key={idx} className="flex items-start gap-3 text-sm">
                      <span className="text-gray-500 font-mono text-xs whitespace-nowrap">
                        {new Date(event.timestamp).toLocaleTimeString()}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        event.type.includes('completed') ? 'bg-green-500/20 text-green-300' :
                        event.type.includes('started') ? 'bg-blue-500/20 text-blue-300' :
                        'bg-gray-500/20 text-gray-300'
                      }`}>
                        {event.type}
                      </span>
                      <span className="text-gray-300">{event.description}</span>
                    </div>
                  ))}
                </div>
              </ResultsSection>
            )}
          </div>
        )}

        {/* Tools Tab */}
        {activeTab === 'tools' && tools && (
          <div className="space-y-6">
            {Object.entries(tools.tools || {}).map(([category, categoryTools]) => (
              <div key={category} className="space-y-3">
                <h3 className="text-lg font-semibold text-white capitalize flex items-center gap-2">
                  <Zap className="w-5 h-5 text-yellow-400" />
                  {category.replace(/_/g, ' ')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {categoryTools.map(tool => (
                    <div key={tool.id} className="bg-gray-800/50 rounded-lg p-4 border border-gray-700 hover:border-cyan-500/50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-white">{tool.name}</h4>
                          <p className="text-sm text-gray-400 mt-1">{tool.description}</p>
                        </div>
                        {tool.mitre_id && (
                          <span className="px-2 py-1 bg-purple-500/20 border border-purple-500/30 rounded text-xs text-purple-300">
                            {tool.mitre_id}
                          </span>
                        )}
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <code className="flex-1 text-xs bg-gray-900 rounded p-2 text-cyan-300 font-mono overflow-x-auto">
                          {tool.command}
                        </code>
                        <button
                          onClick={() => navigator.clipboard.writeText(tool.command)}
                          className="p-2 text-gray-400 hover:text-white"
                          title="Copier"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-3">
            {scans.length > 0 ? (
              scans.map(scan => (
                <div
                  key={scan.id}
                  className="bg-gray-800/50 rounded-lg p-4 border border-gray-700 hover:border-gray-600 cursor-pointer transition-colors"
                  onClick={() => loadScanResults(scan.id)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-white">{scan.target_host}</p>
                      <p className="text-sm text-gray-400">
                        {scan.protocol.toUpperCase()}:{scan.target_port} - {scan.scan_type}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        scan.status === 'completed' ? 'bg-green-500/20 text-green-300' :
                        scan.status === 'running' ? 'bg-blue-500/20 text-blue-300' :
                        scan.status === 'failed' ? 'bg-red-500/20 text-red-300' :
                        'bg-gray-500/20 text-gray-300'
                      }`}>
                        {scan.status}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(scan.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 text-gray-400">
                <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No previous LDAP scan</p>
              </div>
            )}
          </div>
        )}

        {/* AI Exploitation Tab */}
        {activeTab === 'exploit' && scanResults && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-purple-900/30 to-cyan-900/30 rounded-lg p-4 border border-purple-500/30">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-400" />
                Assistance IA pour l'Exploitation
              </h3>
              <p className="text-sm text-gray-400 mt-1">
                Select a vulnerability to get detailed exploitation tips with AI
              </p>
            </div>

            {/* Vulnerability List for Exploitation */}
            {scanResults.results?.vulnerabilities?.length > 0 ? (
              <div className="space-y-3">
                {scanResults.results.vulnerabilities.map((vuln, idx) => (
                  <div
                    key={idx}
                    className={`rounded-lg border p-4 cursor-pointer transition-all hover:scale-[1.01] ${SEVERITY_BG[vuln.severity]}`}
                    onClick={() => {
                      setSelectedVulnerability(vuln);
                      setShowAIPanel(true);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-start gap-3">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${SEVERITY_COLORS[vuln.severity]}`}>
                          {vuln.severity.toUpperCase()}
                        </span>
                        <div>
                          <h4 className="font-semibold text-white">{vuln.name}</h4>
                          <p className="text-sm text-gray-400 mt-1">{vuln.id}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {vuln.mitre_id && (
                          <span className="px-2 py-1 bg-purple-500/20 text-purple-300 text-xs rounded">
                            {vuln.mitre_id}
                          </span>
                        )}
                        <button className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-lg text-sm hover:bg-purple-500/30 flex items-center gap-1">
                          <Brain className="w-4 h-4" />
                          Exploiter avec IA
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No vulnerability detected</p>
                <p className="text-sm mt-2">Launch an LDAP scan to detect vulnerabilities</p>
              </div>
            )}
          </div>
        )}

        {/* Password Cracking Tab */}
        {activeTab === 'cracking' && (
          <PasswordCrackingPanel
            scanId={currentScan}
            workspaceId={workspaceId}
          />
        )}

        {/* Remote Connection Tab */}
        {activeTab === 'connect' && (
          <RemoteConnectionPanel
            targetHost={scanConfig?.target_host || scanResults?.target_host || ''}
            domain={scanConfig?.domain || scanResults?.domain || ''}
            workspaceId={workspaceId}
            credentials={{
              username: scanConfig?.username || '',
              password: scanConfig?.password || ''
            }}
          />
        )}
      </div>

      {/* AI Exploitation Modal */}
      {showAIPanel && selectedVulnerability && (
        <AIExploitationPanel
          vulnerability={selectedVulnerability}
          targetHost={scanConfig?.target_host || scanResults?.target_host || ''}
          targetPort={scanConfig?.target_port || scanResults?.target_port || 389}
          domain={scanConfig?.domain || scanResults?.domain || ''}
          onClose={() => {
            setShowAIPanel(false);
            setSelectedVulnerability(null);
          }}
        />
      )}
    </div>
  );
};

export default LDAPScanPanel;
